/* eslint-disable no-alert */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useMemo,
  useReducer,
  useEffect,
  useContext,
} from 'react';
import produce from 'immer';
import { useIntl } from 'react-intl';
import { AppContext } from '../../App/AppContext';
import { CertContext } from '../../../Context';
import {
  SET_SELECTED_PATIENT,
  SET_SELECTED_DOCTOR,
  SET_PPN,
  SET_WORKFLOW_APPROVALS,
  SET_UNRECONCILED_MEDS,
  SET_COMPILED_APPROVALS,
  SET_COMPILED_APPROVAL_DETAILS,
  SET_PHYSICIAN_APPROVALS,
  NUMBER_OF_DAYS_FOR_UNRECONCILED_MEDS,
} from '../../../constants';
import {
  compileVerbals,
  compileVerbalDetails,
  filterVerbalsByPhys,
  getUserFilter,
} from '../../utils/utils';
import { getDateXDaysBeforeDateAsISOString } from '../../../utils';

const initApprovalContext = {
  loading: true,
  ppn: null,
  selectedPatient: null,
  selectedDoctor: null,
  workFlowApprovals: null,
  unreconciledMeds: null,
  patientPSOs: null,
  // represents the contents of the above three api calls combined
  compiledApprovals: null,
  // OP-31894: one row per unsigned order (detail view of the unsigned orders tab)
  compiledApprovalDetails: null,
  physicianApprovals: null,
  reloadApprovalGrid: null,
};

// maybe change this to add a new data variable (to be called further down in ApprovalListingGrid)
const ApprovalContextReducer = (draft, action) => {
  switch (action.type) {
    case SET_PPN:
      draft.ppn = action.payload;
      break;
    case SET_WORKFLOW_APPROVALS:
      draft.workFlowApprovals = action.payload;
      break;
    case SET_UNRECONCILED_MEDS:
      draft.unreconciledMeds = action.payload;
      break;
    case SET_COMPILED_APPROVALS:
      draft.compiledApprovals = action.payload;
      break;
    case SET_COMPILED_APPROVAL_DETAILS:
      draft.compiledApprovalDetails = action.payload;
      break;
    default:
  }
};

const curriedContextReducer = produce(ApprovalContextReducer);

export const ApprovalContext = createContext(initApprovalContext);
export const ApprovalDispatchContext = createContext({});
export const ApprovalContextConsumer = ApprovalContext.Consumer;
export const ApprovalDispatchContextConsumer = ApprovalDispatchContext.Consumer;

export const ApprovalContextProvider = ({ children }) => {
  const {
    isDoctorRoleGroup,
    userId,
    userNumber,
    agency,
    patient,
    siteId,
  } = useContext(AppContext);
  const { workflow, clearScripts, embedded } = useContext(CertContext);
  const { formatMessage } = useIntl();

  // reset state then fetch data again
  const reloadApprovalGrid = (updatedSiteId) => {
    approvalDispatch({
      type: SET_COMPILED_APPROVALS,
      payload: null,
    });
    approvalDispatch({
      type: SET_COMPILED_APPROVAL_DETAILS,
      payload: null,
    });
    approvalDispatch({
      type: SET_WORKFLOW_APPROVALS,
      payload: null,
    });
    approvalDispatch({
      type: SET_UNRECONCILED_MEDS,
      payload: null,
    });
    approvalDispatch({
      type: SET_SELECTED_PATIENT,
      payload: null,
    });
    approvalDispatch({
      type: SET_SELECTED_DOCTOR,
      payload: null,
    });
    approvalDispatch({
      type: SET_PHYSICIAN_APPROVALS,
      payload: null,
    });
    fetchData(updatedSiteId);
  };

  const createInitialState = () => ({
    workFlowApprovals: null,
    unreconciledMeds: null,
    patientPSOs: null,
    compiledApprovals: null,
    compiledApprovalDetails: null,
    physicianApprovals: null,
    selectedPatient: null,
    selectedDoctor: null,
    reloadApprovalGrid,
  });

  const [approvalState, approvalDispatch] = useReducer(
    curriedContextReducer,
    createInitialState(),
  );

  // Workflow calls
  // add for getting this new data
  const fetchData = (updatedSiteId) => {
    let workflowResponse;
    let clearScriptsResponse;
    const filterByUser = getUserFilter();
    try {
      if (isDoctorRoleGroup) {
        // get verbals for doctor
        (async () => {
          workflowResponse = filterByUser
            ? await workflow.getPhysicianVerbalApprovals(
                userId,
                agency,
                updatedSiteId,
              )
            : await workflow.getVerbalApprovals(agency, updatedSiteId);
          approvalDispatch({
            type: SET_WORKFLOW_APPROVALS,
            payload: workflowResponse,
          });
        })();
        // get meds for doctor
        (async () => {
          clearScriptsResponse = await clearScripts.getUnreconciledMedCounts(
            agency,
            getDateXDaysBeforeDateAsISOString(
              NUMBER_OF_DAYS_FOR_UNRECONCILED_MEDS,
            ),
            userNumber,
            updatedSiteId,
          );
          approvalDispatch({
            type: SET_UNRECONCILED_MEDS,
            payload: clearScriptsResponse,
          });
        })();
      } else {
        // if CERT APP STAND ALONE
        if (!embedded) {
          // get verbals for all patients
          (async () => {
            workflowResponse = await workflow.getVerbalApprovals(
              agency,
              updatedSiteId,
            );
            approvalDispatch({
              type: SET_WORKFLOW_APPROVALS,
              payload: workflowResponse,
            });
          })();
        } else if (patient) {
          // get verbals for all patients
          (async () => {
            workflowResponse = await workflow.getPatientVAApprovals(
              patient.patientNumber,
              agency,
            );
            approvalDispatch({
              type: SET_WORKFLOW_APPROVALS,
              payload: workflowResponse,
            });
          })();
        }
        // get all meds
        (async () => {
          clearScriptsResponse = await clearScripts.getUnreconciledMedCounts(
            agency,
            getDateXDaysBeforeDateAsISOString(
              NUMBER_OF_DAYS_FOR_UNRECONCILED_MEDS,
            ),
            null,
            updatedSiteId,
          );
          approvalDispatch({
            type: SET_UNRECONCILED_MEDS,
            payload: clearScriptsResponse,
          });
        })();
      }
    } catch (error) {
      const { status } = error && error?.response ? error?.response : error;
      if (status === 401) {
        window.alert(
          formatMessage({
            id: 'authError',
          }),
        );
      }
      // certError is Something went wrong, please try again.
      window.alert(
        formatMessage({
          id: 'certError',
        }),
      );
    }
  };

  // on init get responses from workflow and clearscripts
  useEffect(() => {
    fetchData(siteId);
  }, []);

  // once both responses (called in fetchData()) are received, compile them together
  useEffect(() => {
    const { workFlowApprovals, unreconciledMeds } = approvalState;
    if (
      workFlowApprovals &&
      Array.isArray(workFlowApprovals) &&
      unreconciledMeds &&
      Array.isArray(unreconciledMeds)
    ) {
      const compiledVerbals = compileVerbals(
        workFlowApprovals,
        // OP-30839: remove old meds workflow from doclink
        // (!hideMedColumn && unreconciledMeds) || [],
        [],
        // OP-31767: only list by provider if user not a doctor
        // (the sorting does not support new 'All Verbals' setting where a doctor
        // can see approvals from other providers)
        !isDoctorRoleGroup,
      );
      approvalDispatch({
        type: SET_COMPILED_APPROVALS,
        payload: compiledVerbals,
      });
      // OP-31894: same approvals, one row each, for the "By Order" view
      approvalDispatch({
        type: SET_COMPILED_APPROVAL_DETAILS,
        payload: compileVerbalDetails(workFlowApprovals),
      });
    }
  }, [approvalState.workFlowApprovals, approvalState.unreconciledMeds]);

  // when user selects verbals to view, select only the physicians approvals for that
  // that patient
  useEffect(() => {
    const { workFlowApprovals, selectedPatient } = approvalState;
    if (workFlowApprovals && selectedPatient) {
      const filteredVerbals = filterVerbalsByPhys(
        workFlowApprovals,
        selectedPatient,
      );
      approvalDispatch({
        type: SET_PHYSICIAN_APPROVALS,
        payload: filteredVerbals,
      });
    }
  }, [approvalState.selectedPatient, approvalState.selectedDoctor]);

  const DispatchValue = useMemo(
    () => ({
      approvalDispatch,
    }),
    [approvalDispatch],
  );

  return (
    <ApprovalContext.Provider value={approvalState}>
      <ApprovalDispatchContext.Provider value={DispatchValue}>
        {children}
      </ApprovalDispatchContext.Provider>
    </ApprovalContext.Provider>
  );
};

// returns a react component (hence accept props)
// called by Listing
export function withApprovalContext(Component) {
  return function contextComponent(props) {
    return (
      <ApprovalContextConsumer>
        {(context) => (
          <ApprovalDispatchContextConsumer>
            {(dispatchContext) => (
              <Component {...props} {...context} {...dispatchContext} />
            )}
          </ApprovalDispatchContextConsumer>
        )}
      </ApprovalContextConsumer>
    );
  };
}
